/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Box, Dialog, styled, Stack, Typography } from "@mui/material";
import { DetailPanel, ClosureInfo } from "./DetailPanel";
import { Map } from "components/Map/Map";
import { NodeMapOverlay } from "components/Map/NodeMapOverlay";
import { useMapDataContext } from "context/MapDataProvider";
import { ToolPalette, ToolType } from "./ToolPalette";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SpinnerDelay } from "components/Common/SpinnerDelay";
import {
  AwvInfos,
  BodyJobsetApproval,
  MapApi,
  MapData,
  MapDataFromJSON,
  MapDataToJSON,
  MapEdgeType,
  MapNodeType,
  ResultTaskList,
  TasksApi,
  UpdateMap200ResponseOptimizeJobSetsInner,
} from "client";
import { useUserContext } from "context/UserContext";
import {
  isClosed,
  setClosed,
  getElementType,
  RouteElementSummary,
  findRouteElement,
} from "utils/Map";
import {
  ConfirmDialog,
  ConfirmDialogProps,
  ConfirmDialogTitle,
} from "components/Common/ConfirmDialog";
import { useTranslation } from "react-i18next";
import { Const } from "const";
import {
  SystemErrorDialog,
  SystemErrorDialogProps,
} from "components/Common/SystemErrorDialog";
import { getErrorResponse } from "utils/Api";
import { useApiWithTimeout } from "hooks/ApiWrapperWithTimeout";
import moment from "moment";
import { useAwvDataContext } from "context/AwvDataProvider";
import { TimeoutError } from "exception/TimeoutError";
import { useWaitTaskStatus } from "hooks/WaitTaskStatusHook";

interface Props {
  /** Dialog open or not */
  open: boolean;
  /** Action on dialog will close */
  onClose: (isSaved: boolean) => void;
}

/**
 * Component of edit traffic dialog
 * @param{Props} param0 Properties
 * @return{FC} Component
 */
export const EditTrafficDialog: FC<Props> = ({ open, onClose }: Props) => {
  // **************************************
  // States
  // **************************************
  const { mapData, setMapData, selectedRouteElement, setSelectedRouteElement } =
    useMapDataContext();
  const { awvData } = useAwvDataContext();
  // Loading
  const [loading, setLoading] = useState(false);
  // map data
  const [mapToEdit, setMapToEdit] = useState<MapData>(cloneMap(mapData));
  // Current selected tool
  const [currentTool, setCurrentTool] = useState<ToolType>("select");
  // Current selected route closure data
  const [currentClosure, setCurrentClosure] = useState<ClosureInfo>();
  // Contents of confirm dialog
  const [confirmContents, setConfirmContents] = useState<ConfirmDialogProps>();
  // Is there any difference between initial map and current map or not
  const [isDiff, setIsDiff] = useState(false);
  const [showApiError, setShowApiError] = useState<SystemErrorDialogProps>();

  const [getMapApi, cancelGetMapApi] = useApiWithTimeout(
    MapApi,
    Const.operationTimeout.getMapInfo
  );
  const [saveMapApi, cancelSaveMapApi] = useApiWithTimeout(
    MapApi,
    Const.operationTimeout.saveRoadClosure
  );
  const [approveTasksApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.approveTask
  );
  const [getTasksApi, cancelGetTaskApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.getTask
  );
  const [getReCalcTasksApi, cancelGetReCalcTaskApi] = useApiWithTimeout(
    TasksApi,
    Const.operationTimeout.saveRoadClosure
  );

  // **************************************
  // Refs
  // **************************************

  // Initial map data
  const initialMap = useRef<MapData>(cloneMap(mapData));
  // Edited route element
  const editHistory = useRef<RouteElementSummary[]>([]);
  // Is in saving process or not
  const saving = useRef(false);
  // Timer ID for timeout
  const timerId = useRef(0);
  // AWV data
  const awvInfosRef = useRef<AwvInfos>(awvData);

  const { jobsite } = useUserContext();
  const { t } = useTranslation();

  // **************************************
  // Callbacks
  // **************************************

  /**
   * Is given route element edited or not
   * @param {RouteElementSummary} Summary of route element
   * @return {boolean} true: Edited / false: Not edited
   */
  const isEdited = useCallback(
    (elementSummary: RouteElementSummary) => {
      const initial = findRouteElement(initialMap.current, elementSummary);
      const current = findRouteElement(mapToEdit, elementSummary);
      if (initial !== undefined && current !== undefined) {
        return isRouteEdited(initial, current);
      }
    },
    [mapToEdit]
  );

  /**
   * Create road closure display data
   * @param {MapNodeType} element Node
   * @param {MapData} map Map
   * @return {ClosureInfo} Road closure
   */
  const createRouteClosure = useCallback(
    (element?: MapNodeType | MapEdgeType): ClosureInfo | undefined => {
      if (element !== undefined) {
        const originElement = findRouteElement(initialMap.current, {
          type: getElementType(element),
          id: element.id ?? 0,
        });
        const unixTime = mapToEdit.updatedAt ?? mapToEdit.createdAt;
        return {
          position: getClosurePosition(element, mapToEdit),
          isNew: originElement?.priority !== element.priority,
          inputUser: mapToEdit.updateUser,
          inputDate: unixTime ? moment(unixTime).toDate() : undefined,
        };
      }
    },
    [mapToEdit]
  );

  const raiseTimeoutError = useCallback(
    (onClosed?: () => void) => {
      setConfirmContents({
        open: true,
        title: <ConfirmDialogTitle type="Alert" />,
        message: t("AWVUI-MSG166"),
        positiveButtonText: t("AWVUI-MSG22"),
        onPositiveButtonClick: () => {
          onClosed?.();
          setConfirmContents(undefined);
        },
      });
    },
    [t]
  );

  /**
   * Display error dialog for API error
   * @param {ResponseError} error Error object
   * @param {func} onClosed Action on after message dialog closed
   */
  const raiseApiError = useCallback(
    (error: unknown | undefined, onClosed?: () => void) => {
      if (error) {
        if (error instanceof TimeoutError) {
          raiseTimeoutError(onClosed);
        } else {
          setShowApiError({
            open: true,
            error: error,
            onClose: () => {
              setShowApiError(undefined);
              onClosed?.();
            },
          });
        }
      }
    },
    [raiseTimeoutError]
  );

  /**
   * Declaration of save process finish
   * @param {boolean} existsRunningTask true: Show information dialog / false: Don't show information dialog
   * @param {MapData} latestMap
   */
  const finishSave = useCallback(
    (existsRunningTask: boolean, latestMap: MapData | undefined) => {
      saving.current = false;
      setLoading(false);
      stopTimer();
      editHistory.current = [];

      if (existsRunningTask) {
        // If running task exists
        setConfirmContents({
          open: true,
          title: <ConfirmDialogTitle type="Information" />,
          message: t("AWVUI-MSG93"),
          positiveButtonText: t("AWVUI-MSG22"),
          onPositiveButtonClick: () => {
            if (latestMap) {
              // update map
              setMapData(latestMap);
            }
            // Close information dialog
            setConfirmContents(undefined);
            // Close main dialog
            onClose(true);
          },
        });
      } else {
        if (latestMap) {
          // update map
          setMapData(latestMap);
        }
        // Close information dialog
        setConfirmContents(undefined);
        // Close main dialog
        onClose(true);
      }
    },
    [onClose, setMapData, t]
  );

  // Memoization to prevent re-rendering
  const mapProps = useMemo(() => {
    return {
      mapContainerStyle: {
        height: "100%",
        width: "100%",
      },
      options: {
        disableDefaultUI: true,
        mapTypeControl: true,
        zoomControl: true,
        gestureHandling: "greedy",
        keyboardShortcuts: false,
        mapId: process.env.REACT_APP_GOOGLEMAP_MAPID,
      },
      center: {
        lat: jobsite?.latitude ?? 0,
        lng: jobsite?.longitude ?? 0,
      },
      zoom: jobsite?.zoom ?? 11,
    };
  }, [jobsite]);

  const waitTaskStatus = useWaitTaskStatus();

  // **************************************
  // Effects
  // **************************************

  // Effect for dialog opened
  useEffect(() => {
    if (open) {
      console.info("EditTrafficDialog init");
      // Select initial items
      setCurrentTool("select");
      setMapToEdit(cloneMap(mapData));
      initialMap.current = cloneMap(mapData);
      editHistory.current = [];
      saving.current = false;
      timerId.current = 0;
      awvInfosRef.current = awvData;
    }

    return () => {
      stopTimer();
    };
    // Use initialization data instead of the latest.(mapData and awvData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Effect for route element selected
  useEffect(() => {
    setCurrentClosure(createRouteClosure(selectedRouteElement));
  }, [createRouteClosure, selectedRouteElement]);

  // Effect for map edited
  useEffect(() => {
    console.debug("mapToEdit: %o", mapToEdit);
    // Update diff
    setIsDiff(editHistory.current.some((h) => isEdited(h)));
  }, [mapToEdit, isEdited]);

  const isRouteEdited = (
    initial: MapNodeType | MapEdgeType,
    current: MapNodeType | MapEdgeType
  ) => {
    return initial.priority !== current.priority;
  };

  /**
   * Action on cancel button clicked
   */
  const handleCancel = () => {
    console.info("EditTrafficDialog cancel");
    // Clear states
    setCurrentTool("select");
    setMapToEdit(initialMap.current);
    setCurrentClosure(undefined);
    setIsDiff(false);
    setSelectedRouteElement(undefined);

    onClose(false);
  };

  /**
   * Action on tool changed
   * @param{ToolType} tool Selected tool type
   */
  const handleToolChanged = (tool: ToolType) => {
    console.debug(`tool: ${tool}`);
    if (tool === null) {
      // If all tools are unselected, reset to current selected tool
      tool = currentTool;
    }
    setCurrentTool(tool);
    if (tool !== currentTool && tool !== "select") {
      setSelectedRouteElement(undefined);
    }
  };

  /**
   * Change edge is closed.
   * @param{MapNodeType} element Target node
   * @return{MapNodeType} Changed node
   */
  const closeEdge = useCallback((element: MapNodeType | MapEdgeType) => {
    console.debug("element: %o", element);
    if (element.id !== undefined) {
      let changedElement: MapNodeType | MapEdgeType | undefined;
      const newHistory = { type: getElementType(element), id: element.id };
      editHistory.current.push(newHistory);
      setMapToEdit((current) => {
        // Find target node
        const mapEdited = cloneMap(current);
        changedElement = findRouteElement(mapEdited, newHistory);
        console.debug("changedElement: %o", changedElement);

        // Set to not closed
        if (changedElement) {
          setClosed(changedElement, true);
          if (newHistory.type === "Edge") {
            // Find other direction edge
            const targetElement = changedElement as MapEdgeType;
            const otherElement = mapEdited.mapInfo
              .flatMap((m) => m.edges)
              .find(
                (e) =>
                  e?.startNodeId === targetElement.endNodeId &&
                  e?.endNodeId === targetElement.startNodeId
              );
            if (otherElement) {
              // Change closed
              editHistory.current.push({
                type: newHistory.type,
                id: otherElement.id!,
              });
              setClosed(otherElement, true);
            }
          }
        }

        // Update map
        return mapEdited;
      });
      return changedElement;
    }
  }, []);

  const openOneWayEdge = useCallback(
    (
      orgElement: MapNodeType | MapEdgeType,
      orgOtherElement: MapNodeType | MapEdgeType,
      changedElement: MapNodeType | MapEdgeType,
      changedOtherElement: MapNodeType | MapEdgeType
    ) => {
      const newHistory = {
        type: getElementType(changedElement),
        id: changedElement.id!,
      };
      const newOtherHistory = {
        type: getElementType(changedOtherElement),
        id: changedOtherElement.id!,
      };
      const orgPriorty = orgElement?.priority;
      const orgOthrePriorty = orgOtherElement?.priority;
      if (orgPriorty !== orgOthrePriorty) {
        // One way
        if (orgPriorty === Const.priorityClosed) {
          // One way Not Closed (changedOtherElement)
          editHistory.current.push(newOtherHistory);
          setClosed(changedOtherElement, false);
        } else {
          // One way Not Closed (changedElement)
          editHistory.current.push(newHistory);
          setClosed(changedElement, false);
        }
      } else {
        // both way Not Closed
        editHistory.current.push(newHistory);
        setClosed(changedElement, false);
        editHistory.current.push(newOtherHistory);
        setClosed(changedOtherElement, false);
      }
    },
    []
  );

  const openEdgeProc = useCallback(
    (
      changedElement: MapNodeType | MapEdgeType,
      orgElement: MapNodeType | MapEdgeType,
      mapEdited: MapData,
      newHistory: RouteElementSummary
    ) => {
      // Find other direction edge
      const targetElement = changedElement as MapEdgeType;
      const otherElement = mapEdited.mapInfo
        .flatMap((m) => m.edges)
        .find(
          (e) =>
            e?.startNodeId === targetElement.endNodeId &&
            e?.endNodeId === targetElement.startNodeId
        );
      if (otherElement && otherElement.id) {
        const newOtherHistory = {
          type: getElementType(otherElement),
          id: otherElement.id,
        };
        const changedOtherElement = findRouteElement(
          mapEdited,
          newOtherHistory
        );
        const orgOtherElement = findRouteElement(mapData, newOtherHistory);
        if (changedOtherElement && orgOtherElement) {
          openOneWayEdge(
            orgElement,
            orgOtherElement,
            changedElement,
            changedOtherElement
          );
        }
      } else {
        setClosed(changedElement, false);
        editHistory.current.push(newHistory);
      }
    },
    [mapData, openOneWayEdge]
  );

  /**
   * Change edge is opened.
   * @param{MapNodeType} element Target node
   * @return{MapNodeType} Changed node
   */
  const openEdge = useCallback(
    (element: MapNodeType | MapEdgeType) => {
      console.debug("element: %o", element);
      if (element.id !== undefined) {
        let changedElement: MapNodeType | MapEdgeType | undefined;
        const newHistory = { type: getElementType(element), id: element.id };
        setMapToEdit((current) => {
          // Find target node
          const mapEdited = cloneMap(current);
          changedElement = findRouteElement(mapEdited, newHistory);
          const orgElement = findRouteElement(mapData, newHistory);
          console.debug("changedElement: %o", changedElement);
          if (changedElement && orgElement) {
            openEdgeProc(changedElement, orgElement, mapEdited, newHistory);
          }

          // Update map
          return mapEdited;
        });
        console.debug("editHistory: %o", editHistory.current);
        return changedElement;
      }
    },
    [mapData, openEdgeProc]
  );

  /**
   * Action on Road closure clicked
   * @param{MapNodeType} element Clicked node
   */
  const handleRoadClosureClicked = useCallback(
    (element: MapNodeType | MapEdgeType) => {
      console.debug("element: %o", element);
      switch (currentTool) {
        case "select":
          if (element.id === selectedRouteElement?.id) {
            // if already selected, clear selection
            setSelectedRouteElement(undefined);
          } else {
            // Update selection
            setSelectedRouteElement(element);
          }
          break;
        case "delete":
          // Set to not closed
          openEdge(element);
          break;
        default:
          break;
      }
    },
    [openEdge, currentTool, selectedRouteElement?.id, setSelectedRouteElement]
  );

  /**
   * Action on node/edge clicked
   * @param{MapNodeType} element Clicked node
   */
  const handleRoadClicked = useCallback(
    (element: MapNodeType | MapEdgeType) => {
      console.debug("element: %o", element);
      if (currentTool === "stop" && getElementType(element) === "Edge") {
        setSelectedRouteElement(closeEdge(element));
      }
    },
    [closeEdge, currentTool, setSelectedRouteElement]
  );

  /**
   * Action on node clicked
   * @param{MapNodeType} element Clicked node
   */
  const handleElementClicked = useCallback(
    (element: MapNodeType | MapEdgeType) => {
      console.debug("element: %o", element);
      if (getElementType(element) === "Edge") {
        if (isClosed(element)) {
          // Road closure clicked
          handleRoadClosureClicked(element);
        } else {
          // Node clicked
          handleRoadClicked(element);
        }
      }
    },
    [handleRoadClicked, handleRoadClosureClicked]
  );

  /**
   * Action on delete button clicked
   */
  const handleDeleteClicked = () => {
    if (selectedRouteElement) {
      // Display confirm dialog
      setConfirmContents({
        open: true,
        message: t("AWVUI-MSG135"),
        caution: true,
        positiveButtonText: t("AWVUI-MSG13"),
        negativeButtonText: t("AWVUI-MSG12"),
        onPositiveButtonClick: () => {
          // Set to not closed
          openEdge(selectedRouteElement);
          // Clear selection
          setSelectedRouteElement(undefined);
          // Close confirm dialog
          setConfirmContents(undefined);
        },
        onNegativeButtonClick: () => {
          // Close confirm dialog
          setConfirmContents(undefined);
        },
      });
    }
  };

  /**
   * Action on save button clicked
   */
  const handleSaveClicked = () => {
    // Display confirm dialog
    setConfirmContents({
      open: true,
      message: t("AWVUI-MSG138"),
      positiveButtonText: t("AWVUI-MSG14"),
      negativeButtonText: t("AWVUI-MSG15"),
      onPositiveButtonClick: () => {
        // Close confirm dialog
        setConfirmContents(undefined);
        save();
      },
      onNegativeButtonClick: () => {
        // Close confirm dialog
        setConfirmContents(undefined);
      },
    });
  };

  /**
   * Save map
   */
  const save = async () => {
    startSave();
    // Update map
    const edges = mapToEdit.mapInfo
      .flatMap((m) => m.edges)
      .filter(
        (elm): elm is Exclude<typeof elm, undefined> => elm !== undefined
      );
    console.debug("edges: %o", edges);
    let existsRunning = false;
    try {
      // Get running tasks
      const tasks = await getTasks(mapToEdit);
      existsRunning = taskExists(tasks, Const.taskStatus.running);
    } catch (error: unknown) {
      console.error(error);
      if (saving.current) {
        // If saving process is not timed out
        // Abort process
        abortSave();
        raiseApiError(error);
      }
      return;
    }
    let optimizationJobSets;
    if (edges) {
      try {
        const res = await saveMapApi.updateMap({
          mapId: mapToEdit.mapId,
          mapVer: mapToEdit.mapVer,
          mapUpdateData: {
            mapName: mapToEdit.mapName,
            geoSiteId: jobsite?.geoSiteId ?? "",
            originLat: mapToEdit.originLat,
            originLon: mapToEdit.originLon,
            originAlt: mapToEdit.originAlt,
            edgeUpdateInfo: edges,
          },
        });
        optimizationJobSets = res.optimizeJobSets;
      } catch (error: any) {
        console.error(error);
        if (saving.current) {
          // If saving process is not timed out
          // Abort process
          abortSave();
          await handleSaveMapError(error);
        }
        return;
      }
    }
    // Succeed Update map
    await handleSaveMapSucceed(existsRunning, optimizationJobSets);
  };

  // Processing after successful map update
  const handleSaveMapSucceed = async (
    existsRunning: boolean,
    optimizationJobSets?: Array<UpdateMap200ResponseOptimizeJobSetsInner>
  ) => {
    let latestMap: MapData | undefined = undefined;
    try {
      // Get latest map
      latestMap = await getMapApi.getMapInfo({
        mapId: jobsite?.mapId ?? "",
        jobSiteId: jobsite?.jobSiteId ?? "",
      });
      // Get unapproved tasks
      const tasks = optimizationJobSets?.length
        ? await getBatchTasks(latestMap, optimizationJobSets)
        : [];

      // Abort if saving process timed out
      if (!saving.current) {
        return;
      }
      if (tasks.length > 0) {
        // Stop timer for timeout.
        stopTimer();

        // Show batch approve dialog
        setConfirmContents({
          open: true,
          message: t("AWVUI-MSG164"),
          positiveButtonText: t("AWVUI-MSG14"),
          onPositiveButtonClick: async () => {
            // Close confirm dialog
            setConfirmContents(undefined);
            // Batch approve tasks
            try {
              await batchApproveTasks(tasks);
              finishSave(existsRunning, latestMap);
            } catch (error: any) {
              console.error(error);
              raiseApiError(error, () => finishSave(existsRunning, latestMap));
            }
          },
          negativeButtonText: t("AWVUI-MSG15"),
          onNegativeButtonClick: () => finishSave(existsRunning, latestMap),
        });
      } else {
        finishSave(existsRunning, latestMap);
      }
    } catch (error: any) {
      console.error(error);
      if (saving.current) {
        // If saving process is not timed out
        // Finish process
        raiseApiError(error, () => finishSave(existsRunning, latestMap));
      }
    }
  };

  // handle map Save error
  const handleSaveMapError = async (error: unknown) => {
    const responseError = await getErrorResponse(error);

    if (responseError) {
      if (responseError.errors?.includes(Const.errorCodes.mapUnlockFailed)) {
        // In case of failed to unlock

        // Show alert
        setConfirmContents({
          open: true,
          title: <ConfirmDialogTitle type="Alert" />,
          message: t("AWVUI-MSG75"),
          positiveButtonText: t("AWVUI-MSG22"),
          onPositiveButtonClick: () => setConfirmContents(undefined),
        });
        return;
      } else if (
        // In case of map updating failed due to conflict
        responseError.errors?.some((e) =>
          [
            Const.errorCodes.mapUpdateConflict,
            Const.errorCodes.mapVerIsNotLatest,
          ].includes(e)
        )
      ) {
        // In case conflict map update
        setConfirmContents({
          open: true,
          message: t("AWVUI-MSG76"),
          positiveButtonText: t("AWVUI-MSG11"),
          onPositiveButtonClick: async () => {
            try {
              // Close confirm
              setConfirmContents(undefined);
              // Get latest map and merge
              await pullMap();
            } catch (errorPullMap: any) {
              console.error(errorPullMap);
              raiseApiError(errorPullMap);
            }
          },
        });
        return;
      }
    }
    // In case other error
    setConfirmContents({
      open: true,
      title: <ConfirmDialogTitle type="Alert" />,
      message: (
        <Stack
          width={"100%"}
          direction="column"
          textAlign="left"
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Typography variant="inherit" sx={{ whiteSpace: "pre-wrap" }}>
            {t("AWVUI-MSG92")}
          </Typography>
          <Box sx={{ m: "1em" }}>
            {responseError && (
              <Typography variant="inherit">
                {`${t("AWVUI-MSG100")} ${responseError.statusCode}`}
              </Typography>
            )}
            {responseError?.errors && (
              <Typography variant="inherit">
                {`${t("AWVUI-MSG101")} ${responseError.errors}`}
              </Typography>
            )}
          </Box>
        </Stack>
      ),
      positiveButtonText: t("AWVUI-MSG22"),
      onPositiveButtonClick: () => setConfirmContents(undefined),
    });
  };

  /**
   * Declaration of save process start
   */
  const startSave = () => {
    saving.current = true;
    setLoading(true);

    // Set timer for timeout
    timerId.current = window.setTimeout(() => {
      if (saving.current) {
        saving.current = false;
        setLoading(false);
        raiseTimeoutError();
        cancelGetTaskApi();
        cancelGetReCalcTaskApi();
        cancelSaveMapApi();
        cancelGetMapApi();
      }
    }, Const.operationTimeout.saveRoadClosure);
  };

  /**
   * Declaration of save process abort
   */
  const abortSave = () => {
    saving.current = false;
    setLoading(false);
    stopTimer();
    setConfirmContents(undefined);
  };

  /**
   * Stop timer
   */
  const stopTimer = () => {
    if (timerId.current > 0) {
      window.clearTimeout(timerId.current);
      timerId.current = 0;
    }
  };

  /**
   * Batch approve tasks
   * @param {ResultTaskList[]} targetTasks tasks for approve
   */
  const batchApproveTasks = async (targetTasks: ResultTaskList[]) => {
    const bodyJobsetApproval: BodyJobsetApproval[] = targetTasks
      .filter((task) => task.jobSetId !== undefined)
      .map((task) => {
        return { jobSetId: task.jobSetId!, jobSetApproval: true };
      });
    await approveTasksApi.approveTask({
      bodyJobsetApproval: bodyJobsetApproval,
    });
    for (const jobSets of bodyJobsetApproval) {
      await waitTaskStatus(
        jobSets.jobSetId,
        [3],
        Const.operationTimeout.changeApproved
      );
    }
  };

  /**
   * Get tasks
   * @param {MapData} map
   * @return {ResultTaskList[]} tasks
   */
  const getTasks = async (map: MapData) => {
    if (!jobsite?.jobSiteId) {
      console.info("Jobsite ID is not set.");
      return [] as ResultTaskList[];
    }
    if (!awvData.awvInfos?.length) {
      console.info("AWVs is empty.");
      return [] as ResultTaskList[];
    }
    const frameTypeAndNumbers = awvData.awvInfos
      .map((a) => a.frameTypeAndNumber)
      .filter((f) => f !== undefined) as string[];

    // Get Tasks
    const tasks = await getTasksApi.getTask({
      frameTypeAndNumberList: frameTypeAndNumbers,
      mapId: map.mapId,
      date: moment.utc(moment().format("YYYY-MM-DD")).toDate(),
      timezone: jobsite?.timezone,
    });

    if (!tasks?.taskInfos) {
      console.info("tasks is empty.");
      return [] as ResultTaskList[];
    }

    return tasks.taskInfos;
  };

  /**
   * Get batch tasks
   * @param {MapData} map
   * @param {Array<UpdateMap200ResponseOptimizeJobSetsInner>} optimizationJobSets
   * @return {ResultTaskList[]} tasks
   */
  const getBatchTasks = async (
    map: MapData,
    optimizationJobSets: Array<UpdateMap200ResponseOptimizeJobSetsInner>
  ) => {
    // Get Tasks
    const tasks = await getReCalcTasksApi.getBatchTask({
      bodyOptimizeJobSets: {
        mapId: map.mapId,
        optimizeJobSets: optimizationJobSets,
      },
    });

    if (!tasks?.taskInfos) {
      console.info("tasks is empty.");
      return [] as ResultTaskList[];
    }

    return tasks.taskInfos;
  };

  /**
   * Check if there are any tasks with the given status
   * @param {ResultTaskList[]} tasks Tasks
   * @param {number} taskStatus Task status
   * @return {boolean} true: exists / false: not exists.
   */
  const taskExists = (tasks: ResultTaskList[], taskStatus: number) => {
    const filtered = tasks.filter((task) => task.taskStatus === taskStatus);
    return filtered.length > 0;
  };

  /**
   * Get latest map and merge edit
   */
  const pullMap = async () => {
    // Get latest map
    const latestMap = await getMapApi.getMapInfo({
      mapId: jobsite?.mapId ?? "",
      jobSiteId: jobsite?.jobSiteId ?? "",
    });

    // Get edited node/edge
    const editedRouteElement = editHistory.current.map((h) =>
      findRouteElement(mapToEdit, h)
    );

    // Reflect edit to latest map
    editedRouteElement.forEach((r) => {
      if (r) {
        const latestElement = findRouteElement(latestMap, {
          type: getElementType(r),
          id: r.id ?? 0,
        });
        if (latestElement !== undefined && isRouteEdited(latestElement, r)) {
          setClosed(latestElement, isClosed(r));
        }
      }
    });

    // Update display
    setMapToEdit(latestMap);
  };

  return (
    <Dialog
      maxWidth={false}
      fullWidth
      open={open}
      PaperProps={{ sx: { height: "100%" } }}
    >
      <Container>
        <MapArea>
          <Map {...mapProps}>
            <NodeMapOverlay
              mapData={mapToEdit}
              onNodeSelect={handleElementClicked}
              selected={selectedRouteElement}
            />
          </Map>
          <ToolPaletteArea>
            <ToolPalette
              currentTool={currentTool}
              onCurrentToolChanged={handleToolChanged}
            />
          </ToolPaletteArea>
        </MapArea>
        <DetailArea>
          <DetailPanel
            editable
            closure={currentClosure}
            saveEnable={isDiff}
            onDeleteClick={handleDeleteClicked}
            onCancelClick={handleCancel}
            onSaveClick={handleSaveClicked}
          />
        </DetailArea>
      </Container>
      {loading && <SpinnerDelay />}
      {confirmContents && <ConfirmDialog {...confirmContents} />}
      {showApiError && <SystemErrorDialog {...showApiError} />}
    </Dialog>
  );
};

/**
 * Create clone of map
 * @param {MapData} map Source map
 * @return {MapData} Cloned map
 */
function cloneMap(map: MapData): MapData {
  // deep copy.
  // eslint-disable-next-line new-cap
  return MapDataFromJSON(MapDataToJSON(map));
}

/**
 * Get position of route closure
 * @param {MapNodeType | MapEdgeType} element Route element
 * @param {MapData} map Map
 * @return {google.maps.LatLngLiteral} Position
 */
function getClosurePosition(
  element: MapNodeType | MapEdgeType,
  map: MapData
): google.maps.LatLngLiteral {
  switch (getElementType(element)) {
    case "Node":
      const node = element as MapNodeType;
      return {
        lat: node.geolocation ? node.geolocation[1] : 0,
        lng: node.geolocation ? node.geolocation[0] : 0,
      };
    case "Edge":
      const edge = element as MapEdgeType;
      if (edge.startNodeId !== undefined && edge.endNodeId !== undefined) {
        const startNode = findRouteElement(map, {
          type: "Node",
          id: edge.startNodeId,
        });
        const endNode = findRouteElement(map, {
          type: "Node",
          id: edge.endNodeId,
        });
        if (startNode && endNode) {
          const startPosition = getClosurePosition(startNode, map);
          const endPosition = getClosurePosition(endNode, map);
          return {
            lat: (startPosition.lat + endPosition.lat) * 0.5,
            lng: (startPosition.lng + endPosition.lng) * 0.5,
          };
        }
      }
      break;
    default:
      return { lat: 0, lng: 0 };
  }
  return { lat: 0, lng: 0 };
}

const detailPanelWidth = "350px";
const Container = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: "grid",
  gridTemplateRows: "100%",
  gridTemplateColumns: `auto ${detailPanelWidth}`,
  gap: theme.spacing(1, 0),
}));

const MapArea = styled(Box)({
  gridRow: "1",
  gridColumn: "1",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
});

const DetailArea = styled(Box)({
  gridRow: "1",
  gridColumn: "2",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
});

const ToolPaletteArea = styled(Box)({
  gridRow: "1",
  gridColumn: "1",
  alignItems: "center",
  justifyContent: "center",
  display: "flex",
  position: "absolute",
  left: 0,
  top: 0,
  paddingTop: 10,
  width: `calc(100% - ${detailPanelWidth})`,
  pointerEvents: "none", // Disable mouse event for control map UI
});
